import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileDown, FileText, Users, Shirt, Truck, Clock, Factory } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const downloadPdf = async (url, filename) => {
    try {
        const response = await axios.get(url, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success('PDF descargado exitosamente');
    } catch (error) {
        console.error('Error downloading PDF:', error);
        toast.error('Error al descargar el PDF');
    }
};

export const Reportes = () => {
    const [lotes, setLotes] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [selectedLote, setSelectedLote] = useState('');
    const [selectedEmpleado, setSelectedEmpleado] = useState('');
    const [fechaInicio, setFechaInicio] = useState(new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]);
    const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);
    const [downloading, setDownloading] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [lotesRes, empRes] = await Promise.all([
                    axios.get(`${API_URL}/api/lotes`),
                    axios.get(`${API_URL}/api/empleados`)
                ]);
                setLotes(lotesRes.data);
                setEmpleados(empRes.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, []);

    const handleDownload = async (type) => {
        setDownloading(type);
        try {
            switch (type) {
                case 'lote':
                    if (!selectedLote) { toast.error('Seleccione un lote'); break; }
                    await downloadPdf(`${API_URL}/api/reportes/lote/${selectedLote}/pdf`, `reporte_lote.pdf`);
                    break;
                case 'telas':
                    await downloadPdf(`${API_URL}/api/reportes/telas/pdf`, 'inventario_telas.pdf');
                    break;
                case 'proveedores':
                    await downloadPdf(`${API_URL}/api/reportes/proveedores/pdf`, 'proveedores.pdf');
                    break;
                case 'trabajadores':
                    await downloadPdf(`${API_URL}/api/reportes/trabajadores/pdf`, 'cuentas_trabajadores.pdf');
                    break;
                case 'asistencia':
                    const empParam = selectedEmpleado && selectedEmpleado !== 'all' ? `?empleado_id=${selectedEmpleado}` : '';
                    await downloadPdf(`${API_URL}/api/reportes/asistencia/pdf${empParam}`, 'asistencia.pdf');
                    break;
                case 'produccion':
                    if (!fechaInicio || !fechaFin) { toast.error('Seleccione las fechas'); break; }
                    await downloadPdf(
                        `${API_URL}/api/reportes/produccion/pdf?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`,
                        `produccion_${fechaInicio}_${fechaFin}.pdf`
                    );
                    break;
                default:
                    break;
            }
        } finally {
            setDownloading('');
        }
    };

    const reports = [
        {
            id: 'lote',
            title: 'Reporte por Lote',
            description: 'Trazabilidad completa de un lote: telas, cortes, maquila, lavandería y habilitación.',
            icon: FileText,
            color: '#FBBF24',
            bgColor: 'bg-amber-400/10',
            hasSelector: true
        },
        {
            id: 'telas',
            title: 'Inventario de Telas',
            description: 'Stock actual y últimos movimientos de telas.',
            icon: Shirt,
            color: '#22C55E',
            bgColor: 'bg-emerald-400/10'
        },
        {
            id: 'proveedores',
            title: 'Deudas con Proveedores',
            description: 'Resumen de compras, pagos y saldo pendiente por proveedor.',
            icon: Truck,
            color: '#EAB308',
            bgColor: 'bg-yellow-400/10'
        },
        {
            id: 'trabajadores',
            title: 'Cuentas por Pagar - Trabajadores',
            description: 'Trabajo realizado, pagos y saldo pendiente por trabajador.',
            icon: Users,
            color: '#EF4444',
            bgColor: 'bg-red-400/10'
        },
        {
            id: 'asistencia',
            title: 'Reporte de Asistencia',
            description: 'Registro de asistencia general o por empleado.',
            icon: Clock,
            color: '#8B5CF6',
            bgColor: 'bg-purple-400/10',
            hasEmpleadoSelector: true
        },
        {
            id: 'produccion',
            title: 'Reporte de Producción',
            description: 'Cortes realizados en un periodo de tiempo.',
            icon: Factory,
            color: '#10B981',
            bgColor: 'bg-emerald-400/10',
            hasDateRange: true
        }
    ];

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="reportes-page">
            <div>
                <h1 className="text-2xl font-bold text-zinc-100" >
                    Reportes PDF
                </h1>
                <p className="text-zinc-400 mt-1">Genera y descarga reportes en formato PDF</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {reports.map((report) => {
                    const Icon = report.icon;
                    return (
                        <Card key={report.id} className="card" data-testid={`report-card-${report.id}`}>
                            <CardContent className="p-6">
                                <div className="flex items-start gap-4 mb-4">
                                    <div className={`w-12 h-12 rounded-xl ${report.bgColor} flex items-center justify-center shrink-0`}>
                                        <Icon className="w-6 h-6" style={{ color: report.color }} />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-zinc-100">{report.title}</h3>
                                        <p className="text-sm text-zinc-400 mt-1">{report.description}</p>
                                    </div>
                                </div>

                                {report.hasSelector && (
                                    <div className="mb-4">
                                        <Label className="form-label">Seleccionar Lote</Label>
                                        <Select value={selectedLote} onValueChange={setSelectedLote}>
                                            <SelectTrigger data-testid="select-lote-report">
                                                <SelectValue placeholder="Seleccionar lote" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {lotes.map(l => (
                                                    <SelectItem key={l.id} value={l.id}>{l.numero_lote} - {l.marca}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {report.hasEmpleadoSelector && (
                                    <div className="mb-4">
                                        <Label className="form-label">Empleado (opcional - dejar vacío para reporte general)</Label>
                                        <Select value={selectedEmpleado} onValueChange={setSelectedEmpleado}>
                                            <SelectTrigger data-testid="select-empleado-report">
                                                <SelectValue placeholder="Todos los empleados" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">Todos los empleados</SelectItem>
                                                {empleados.map(e => (
                                                    <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {report.hasDateRange && (
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div>
                                            <Label className="form-label">Desde</Label>
                                            <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} data-testid="fecha-inicio" />
                                        </div>
                                        <div>
                                            <Label className="form-label">Hasta</Label>
                                            <Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} data-testid="fecha-fin" />
                                        </div>
                                    </div>
                                )}

                                <Button
                                    onClick={() => handleDownload(report.id)}
                                    disabled={downloading === report.id}
                                    className="w-full bg-amber-400 hover:bg-amber-500 text-zinc-900"
                                    data-testid={`download-${report.id}`}
                                >
                                    <FileDown className="w-4 h-4 mr-2" />
                                    {downloading === report.id ? 'Generando...' : 'Descargar PDF'}
                                </Button>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};
